import React, { useState, useEffect } from 'react';
import { getCurrencySymbol } from '../utils/currency';
import { FaTrash, FaStar } from 'react-icons/fa';
import { useToast } from '../contexts/ToastContext';
import { readFlatTemplate, fetchCombinations } from '@services/firebaseGlobal';
import { writeUserDocument, moveUserDocument } from '@services/firebaseUser';
import { doc, onSnapshot, serverTimestamp } from 'firebase/firestore';
import { getFirebase } from '@services/firebase';

let db;

const getDb = () => {
  if (!db) {
    const { db: firebaseDb } = getFirebase();
    db = firebaseDb;
  }
  return db;
};

const EnquiryProfileCard = ({
  theme,
  subtitle,
  flavour,
  cover,
  eggless,
  glutenFree,
  sponge,
  quantity,
  ccost,
  costEstimate,
  image,
  deliveryDate,
  deliveryTime,
  address,
  del_mode,
  rev_feed,
  showDeleteIcon = false,
  onDelete,
  onClick,
  onRequestReview,
  onProfileMoved,
  isExpanded = false,
  userId,
  userRole,
  enquiryId,
  collectionName = 'enquiry',
}) => {

  const [otimelineData, setOtimelineData] = useState([]);
  const [processControls, setProcessControls] = useState([]);
  const [exceptionTimelines, setExceptionTimelines] = useState([]);
  const [reqState, setReqState] = useState('');
  const [creatorUserId, setCreatorUserId] = useState('');
  const [currentCollection, setCurrentCollection] = useState(collectionName);
  const [orderReceivedIdNumber, setOrderReceivedIdNumber] = useState(null);
  const showToast = useToast();
  const [showPopup, setShowPopup] = useState(false);
  const [showRejectPopup, setShowRejectPopup] = useState(false);
  const [showFeedbackPopup, setShowFeedbackPopup] = useState(false);
  const [feedbackRating, setFeedbackRating] = useState(0);
  const [feedbackComment, setFeedbackComment] = useState('');
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [showChangeButtons, setShowChangeButtons] = useState(false);
  const [changeAction, setChangeAction] = useState(null);
  const [showOptionDropdown, setShowOptionDropdown] = useState(false);
  const [optionAction, setOptionAction] = useState(null);

  const isNormalUser = userRole === 'Buyer';
  const rolePrefixes = [userRole.toLowerCase()];
  const isAdmin = userRole === 'Admin';
  const isSuper = userRole === 'Super';

  useEffect(() => {
    const fetchOtimelineData = async () => {
      try {
        const data = await readFlatTemplate('otimeline');
        setOtimelineData(data);
        const orderReceivedEntry = data.find(entry => entry.name === 'Order Received');
        if (orderReceivedEntry) {
          const idNumber = parseInt(orderReceivedEntry.id.replace('id', ''), 10);
          setOrderReceivedIdNumber(idNumber);
        }
      } catch (err) {
        console.error('Error fetching otimeline data:', err);
      }
    };

    fetchOtimelineData();
  }, []);

  useEffect(() => {
    const fetchProcessControlData = async () => {
      try {
        const data = await fetchCombinations('oprscontrol');
        setProcessControls(data);
      } catch (err) {
        console.error('Error fetching process control data:', err);
        setProcessControls([]);
      }
    };

    const fetchExceptionTimelineData = async () => {
      try {
        const data = await fetchCombinations('oexptimeline');
        setExceptionTimelines(data);
      } catch (err) {
        console.error('Error fetching exception timeline data:', err);
        setExceptionTimelines([]);
      }
    };

    fetchProcessControlData();
    fetchExceptionTimelineData();
  }, []);

  useEffect(() => {
    const db = getDb();

    if (!userId || !enquiryId || !db || !otimelineData.length || !orderReceivedIdNumber) {
      return;
    }

    let initialCollection = currentCollection;

    const docRef = doc(db, initialCollection, enquiryId);
    const unsubscribe = onSnapshot(
      docRef,
      (docSnapshot) => { // Renamed to avoid confusion with doc2
        if (docSnapshot.exists()) {
          const data = docSnapshot.data();
          const newReqState = data.reqState || 'New Request';
          setReqState(newReqState);
          setCreatorUserId(data.userId || '');
          const stateEntry = otimelineData.find(entry => entry.name === newReqState);
          let correctCollection = 'enquiry';
          if (stateEntry) {
            const stateIdNumber = parseInt(stateEntry.id.replace('id', ''), 10);
            if (stateIdNumber >= orderReceivedIdNumber && newReqState !== 'Request Cancelled') {
              correctCollection = 'order';
            }
          }

          if (correctCollection !== initialCollection) {
            setCurrentCollection(correctCollection);
            return;
          }

          const changeEntry = exceptionTimelines.find(
            entry => entry.id.startsWith('change') && entry.requestState === newReqState
          );
          if (changeEntry) {
            setShowChangeButtons(true);
            setChangeAction(changeEntry);
            setShowOptionDropdown(false);
            setOptionAction(null);
            return;
          } else {
            setShowChangeButtons(false);
            setChangeAction(null);
          }

          const optionEntry = exceptionTimelines.find(
            entry => entry.id.startsWith('option') && entry.requestState === newReqState
          );
          const isActionAllowed = processControls.some(
            control =>
              rolePrefixes.some(p => control.id.startsWith(p)) &&
              control.application === 'Small' &&
              control.activity === 'Action' &&
              control.state === newReqState
          );
          const deliveryDetailsValid = (del_mode === false)
            ? deliveryDate && deliveryTime
            : address && deliveryDate && deliveryTime;
          const isEnquiryButtonEnabledForCheck = isActionAllowed && (!isNormalUser || deliveryDetailsValid);

          if (optionEntry && isEnquiryButtonEnabledForCheck) {
            setShowOptionDropdown(true);
            setOptionAction(optionEntry);
          } else {
            setShowOptionDropdown(false);
            setOptionAction(null);
          }
        } else {
          const otherCollection = initialCollection === 'enquiry' ? 'order' : 'enquiry';
          const otherDocRef = doc(db, otherCollection, enquiryId);
          onSnapshot(
            otherDocRef,
            (otherDocSnapshot) => { // Renamed to avoid confusion
              if (otherDocSnapshot.exists()) {
                setCurrentCollection(otherCollection);
              } else {
                setReqState('New Request');
                setCreatorUserId('');
                setShowChangeButtons(false);
                setChangeAction(null);
                setShowOptionDropdown(false);
                setOptionAction(null);
              }
            },
            (err) => {
              console.error(`Error fetching from ${otherCollection}:`, err.message, err.stack);
              setReqState('New Request');
              setCreatorUserId('');
              setShowChangeButtons(false);
              setChangeAction(null);
              setShowOptionDropdown(false);
              setOptionAction(null);
            }
          );
        }
      },
      (err) => {
        console.error(`Error fetching reqState from ${initialCollection}:`, err.message, err.stack);
        setReqState('New Request');
        setCreatorUserId('');
        setShowChangeButtons(false);
        setChangeAction(null);
        setShowOptionDropdown(false);
        setOptionAction(null);
      }
    );

    return () => unsubscribe();
  }, [userId, enquiryId, collectionName, exceptionTimelines, userRole, processControls, deliveryDate, deliveryTime, address, del_mode, otimelineData, orderReceivedIdNumber, currentCollection]);

  const canEnableButton = (stateToCheck = reqState) => {
    if (isButtonDisabled) return false;

    const applicableControls = processControls.filter(
      control =>
        rolePrefixes.some(p => control.id.startsWith(p)) &&
        control.application === 'Small' &&
        control.activity === 'Action' &&
        control.state === stateToCheck
    );

    return applicableControls.length > 0;
  };

  const canEnableDelete = (stateToCheck = reqState) => {
    const applicableControls = processControls.filter(
      control =>
        rolePrefixes.some(p => control.id.startsWith(p)) &&
        control.application === 'Small' &&
        control.activity === 'Delete' &&
        control.state === stateToCheck
    );

    return applicableControls.length > 0;
  };

  const getStopOrPauseAction = (currentReqState) => {
    const stopEntry = exceptionTimelines.find(
      entry => entry.id.startsWith('stop') && entry.requestState === currentReqState
    );
    if (stopEntry) {
      return { type: 'stop', state: stopEntry.showState1 };
    }

    const pauseEntry = exceptionTimelines.find(
      entry => entry.id.startsWith('pause') && entry.requestState === currentReqState
    );
    if (pauseEntry) {
      return { type: 'pause', state: pauseEntry.showState1 };
    }

    return null;
  };

  const computeNextStateAndButtonText = (currentReqState) => {
    const action = getStopOrPauseAction(currentReqState);
    if (action) {
      return { nextState: action.state, buttonText: action.state, isStopAction: action.type === 'stop' };
    }

    if (!currentReqState || otimelineData.length === 0) {
      return { nextState: 'Request Review', buttonText: 'Request Review', isStopAction: false };
    }

    const currentEntry = otimelineData.find(entry => entry.name === currentReqState);
    if (!currentEntry) {
      return { nextState: 'Request Review', buttonText: 'Request Review', isStopAction: false };
    }

    const currentKeyNumber = parseInt(currentEntry.id.replace('id', ''), 10);
    const nextKey = `id${currentKeyNumber + 1}`;
    const nextEntry = otimelineData.find(entry => entry.id === nextKey);
    const nextState = nextEntry ? nextEntry.name : 'Request Review';

    return { nextState, buttonText: nextState, isStopAction: false };
  };

  const { nextState, buttonText, isStopAction } = computeNextStateAndButtonText(reqState);

  const isPickUpMode = del_mode === false;
  const deliveryDetailsValid = isPickUpMode
    ? deliveryDate && deliveryTime
    : address && deliveryDate && deliveryTime;

  const isEnquiryButtonEnabled = canEnableButton() && (!isNormalUser || deliveryDetailsValid);

  const handleRequestReview = async (e) => {
    e.stopPropagation();
    if (!isEnquiryButtonEnabled) {
      setShowPopup(true);
      return;
    }

    if (nextState === 'Give Feedback') {
      setShowFeedbackPopup(true);
      return;
    }

    try {
      const targetUserId = isNormalUser ? userId : creatorUserId;
      if (!targetUserId) {
        throw new Error('Unable to determine the user ID for updating the profile');
      }

      if (currentCollection === 'enquiry' && nextState === 'Order Received') {
        await moveUserDocument('enquiry', 'order', targetUserId, enquiryId);
        const updateData = { reqState: 'Order Received' };
        await writeUserDocument('order', targetUserId, updateData, enquiryId);

        setCurrentCollection('order');
        if (onProfileMoved) {
          onProfileMoved(enquiryId);
        }
      } else {
        const updateData = { reqState: nextState };
        await writeUserDocument(currentCollection, targetUserId, updateData, enquiryId);

        // Check if the new reqState requires a collection change
        const stateEntry = otimelineData.find(entry => entry.name === nextState);
        if (stateEntry) {
          const stateIdNumber = parseInt(stateEntry.id.replace('id', ''), 10);
          if (stateIdNumber >= orderReceivedIdNumber && nextState !== 'Request Cancelled' && currentCollection === 'enquiry') {
            await moveUserDocument('enquiry', 'order', targetUserId, enquiryId);
            await writeUserDocument('order', targetUserId, updateData, enquiryId);
            setCurrentCollection('order');
            if (onProfileMoved) {
              onProfileMoved(enquiryId);
            }
          } else if ((stateIdNumber < orderReceivedIdNumber || nextState === 'Request Cancelled') && currentCollection === 'order') {
            await moveUserDocument('order', 'enquiry', targetUserId, enquiryId);
            await writeUserDocument('enquiry', targetUserId, updateData, enquiryId);
            setCurrentCollection('enquiry');
          }
        }
      }

      setReqState(nextState);

      if (isStopAction) {
        setIsButtonDisabled(true);
      }

      const optionEntry = exceptionTimelines.find(
        entry => entry.id.startsWith('option') && entry.requestState === nextState
      );
      const isActionAllowed = processControls.some(
        control =>
          rolePrefixes.some(p => control.id.startsWith(p)) &&
          control.application === 'Small' &&
          control.activity === 'Action' &&
          control.state === nextState
      );
      const isEnquiryButtonEnabledForCheck = isActionAllowed && (!isNormalUser || deliveryDetailsValid) && !isStopAction;

      if (optionEntry && isEnquiryButtonEnabledForCheck) {
        setShowOptionDropdown(true);
        setOptionAction(optionEntry);
      } else {
        setShowOptionDropdown(false);
        setOptionAction(null);
      }
    } catch (err) {
      console.error('Error updating reqState:', err.message, err.stack);
      showToast(`Failed to update request state: ${err.message}`);
    }

    if (onRequestReview) {
      onRequestReview({ address });
    }
  };

  const handleFeedbackSubmit = async (e) => {
    e.stopPropagation();

    if (feedbackRating === 0) {
      showToast('Please select a star rating.');
      return;
    }
    if (feedbackRating < 4 && feedbackComment.trim() === '') {
      showToast('Please describe your experience (required for ratings below 4 stars).');
      return;
    }

    try {
      const updateData = {
        reqState: nextState,
        feedback: {
          rating: feedbackRating,
          comment: feedbackComment.trim() || null,
          submittedAt: serverTimestamp(),
        },
      };
      const targetUserId = isNormalUser ? userId : creatorUserId;
      if (!targetUserId) {
        throw new Error('Unable to determine the user ID for updating the profile');
      }

      if (currentCollection === 'enquiry' && nextState === 'Order Received') {
        await moveUserDocument('enquiry', 'order', targetUserId, enquiryId);
        await writeUserDocument('order', targetUserId, updateData, enquiryId);
        setCurrentCollection('order');
        if (onProfileMoved) {
          onProfileMoved(enquiryId);
        }
      } else {
        await writeUserDocument(currentCollection, targetUserId, updateData, enquiryId);
        const stateEntry = otimelineData.find(entry => entry.name === nextState);
        if (stateEntry) {
          const stateIdNumber = parseInt(stateEntry.id.replace('id', ''), 10);
          if (stateIdNumber >= orderReceivedIdNumber && nextState !== 'Request Cancelled' && currentCollection === 'enquiry') {
            await moveUserDocument('enquiry', 'order', targetUserId, enquiryId);
            await writeUserDocument('order', targetUserId, updateData, enquiryId);
            setCurrentCollection('order');
            if (onProfileMoved) {
              onProfileMoved(enquiryId);
            }
          } else if ((stateIdNumber < orderReceivedIdNumber || nextState === 'Request Cancelled') && currentCollection === 'order') {
            await moveUserDocument('order', 'enquiry', targetUserId, enquiryId);
            await writeUserDocument('enquiry', targetUserId, updateData, enquiryId);
            setCurrentCollection('enquiry');
          }
        }
      }

      setReqState(nextState);
      setShowFeedbackPopup(false);
      setFeedbackRating(0);
      setFeedbackComment('');

      const optionEntry = exceptionTimelines.find(
        entry => entry.id.startsWith('option') && entry.requestState === nextState
      );
      const isActionAllowed = processControls.some(
        control =>
          rolePrefixes.some(p => control.id.startsWith(p)) &&
          control.application === 'Small' &&
          control.activity === 'Action' &&
          control.state === nextState
      );
      const isEnquiryButtonEnabledForCheck = isActionAllowed && (!isNormalUser || deliveryDetailsValid);

      if (optionEntry && isEnquiryButtonEnabledForCheck) {
        setShowOptionDropdown(true);
        setOptionAction(optionEntry);
      } else {
        setShowOptionDropdown(false);
        setOptionAction(null);
      }
    } catch (err) {
      console.error('Error submitting feedback:', err.message, err.stack);
      showToast(`Failed to submit feedback: ${err.message}`);
      setShowFeedbackPopup(false);
      setFeedbackRating(0);
      setFeedbackComment('');
    }
  };

  const handleStarClick = (rating) => {
    setFeedbackRating(rating);
  };

  const handleAccept = async (e) => {
    e.stopPropagation();
    if (!changeAction) return;

    try {
      const newReqState = changeAction.showState1;
      const updateData = { reqState: newReqState };
      const targetUserId = isNormalUser ? userId : creatorUserId;
      if (!targetUserId) {
        throw new Error('Unable to determine the user ID for updating the profile');
      }

      if (currentCollection === 'enquiry' && newReqState === 'Order Received') {
        try {
          await moveUserDocument('enquiry', 'order', targetUserId, enquiryId); // Line 395
        } catch (moveError) {
          console.error('moveUserDocument error:', moveError.message, moveError.stack);
          throw moveError;
        }
        await writeUserDocument('order', targetUserId, updateData, enquiryId); // Line 398
        setCurrentCollection('order');
        if (onProfileMoved) {
          onProfileMoved(enquiryId);
        }
      } else {
        await writeUserDocument(currentCollection, targetUserId, updateData, enquiryId); // Line 405
        const stateEntry = otimelineData.find(entry => entry.name === newReqState);
        if (stateEntry) {
          const stateIdNumber = parseInt(stateEntry.id.replace('id', ''), 10);
          if (stateIdNumber >= orderReceivedIdNumber && newReqState !== 'Request Cancelled' && currentCollection === 'enquiry') {
            try {
              await moveUserDocument('enquiry', 'order', targetUserId, enquiryId); // Line 413
            } catch (moveError) {
              console.error('moveUserDocument error:', moveError.message, moveError.stack);
              throw moveError;
            }
            await writeUserDocument('order', targetUserId, updateData, enquiryId); // Line 416
            setCurrentCollection('order');
            if (onProfileMoved) {
              onProfileMoved(enquiryId);
            }
          } else if ((stateIdNumber < orderReceivedIdNumber || newReqState === 'Request Cancelled') && currentCollection === 'order') {
            try {
              await moveUserDocument('order', 'enquiry', targetUserId, enquiryId); // Line 422
            } catch (moveError) {
              console.error('moveUserDocument error:', moveError.message, moveError.stack);
              throw moveError;
            }
            await writeUserDocument('enquiry', targetUserId, updateData, enquiryId); // Line 425
            setCurrentCollection('enquiry');
          }
        }
      }

      setReqState(newReqState);
      setShowChangeButtons(false);
      setChangeAction(null);
    } catch (err) {
      console.error('Error accepting change request:', err.message, err.stack);
      showToast(`Failed to accept change request: ${err.message}`);
    }
  };

  const handleRejectClick = (e) => {
    e.stopPropagation();
    setShowRejectPopup(true);
  };

  const handleRejectConfirm = async () => {
    if (!changeAction) return;

    try {
      const newReqState = changeAction.showState2;
      const updateData = { reqState: newReqState };
      const targetUserId = isNormalUser ? userId : creatorUserId;
      if (!targetUserId) {
        throw new Error('Unable to determine the user ID for updating the profile');
      }

      if (currentCollection === 'enquiry' && newReqState === 'Order Received') {
        await moveUserDocument('enquiry', 'order', targetUserId, enquiryId);
        await writeUserDocument('order', targetUserId, updateData, enquiryId);

        setCurrentCollection('order');
        if (onProfileMoved) {
          onProfileMoved(enquiryId);
        }
      } else {
        await writeUserDocument(currentCollection, targetUserId, updateData, enquiryId);
        const stateEntry = otimelineData.find(entry => entry.name === newReqState);
        if (stateEntry) {
          const stateIdNumber = parseInt(stateEntry.id.replace('id', ''), 10);
          if (stateIdNumber >= orderReceivedIdNumber && newReqState !== 'Request Cancelled' && currentCollection === 'enquiry') {
            await moveUserDocument('enquiry', 'order', targetUserId, enquiryId);
            await writeUserDocument('order', targetUserId, updateData, enquiryId);
            setCurrentCollection('order');
            if (onProfileMoved) {
              onProfileMoved(enquiryId);
            }
          } else if ((stateIdNumber < orderReceivedIdNumber || newReqState === 'Request Cancelled') && currentCollection === 'order') {
            await moveUserDocument('order', 'enquiry', targetUserId, enquiryId);
            await writeUserDocument('enquiry', targetUserId, updateData, enquiryId);
            setCurrentCollection('enquiry');
          }
        }
      }

      setReqState(newReqState);
      setShowChangeButtons(false);
      setChangeAction(null);
      setShowRejectPopup(false);
    } catch (err) {
      console.error('Error rejecting change request:', err.message, err.stack);
      showToast(`Failed to reject change request: ${err.message}`);
      setShowRejectPopup(false);
    }
  };

  const handleOptionSelect = async (e) => {
    e.stopPropagation();
    const selectedReqState = e.target.value;
    if (!selectedReqState || !optionAction) return;

    try {
      const targetUserId = isNormalUser ? userId : creatorUserId;
      if (!targetUserId) {
        throw new Error('Unable to determine the user ID for updating the profile');
      }

      let finalReqState = selectedReqState;
      let isStopActionTriggered = false;

      const action = getStopOrPauseAction(selectedReqState);
      if (action) {
        finalReqState = action.state;
        isStopActionTriggered = action.type === 'stop';
      }

      if (currentCollection === 'enquiry' && finalReqState === 'Order Received') {
        await moveUserDocument('enquiry', 'order', targetUserId, enquiryId);
        const updateData = { reqState: 'Order Received' };
        await writeUserDocument('order', targetUserId, updateData, enquiryId);
        setCurrentCollection('order');
        if (onProfileMoved) {
          onProfileMoved(enquiryId);
        }
      } else {
        const updateData = { reqState: finalReqState };
        await writeUserDocument(currentCollection, targetUserId, updateData, enquiryId);
        const stateEntry = otimelineData.find(entry => entry.name === finalReqState);
        if (stateEntry) {
          const stateIdNumber = parseInt(stateEntry.id.replace('id', ''), 10);
          if (stateIdNumber >= orderReceivedIdNumber && finalReqState !== 'Request Cancelled' && currentCollection === 'enquiry') {
            await moveUserDocument('enquiry', 'order', targetUserId, enquiryId);
            await writeUserDocument('order', targetUserId, updateData, enquiryId);
            setCurrentCollection('order');
            if (onProfileMoved) {
              onProfileMoved(enquiryId);
            }
          } else if ((stateIdNumber < orderReceivedIdNumber || finalReqState === 'Request Cancelled') && currentCollection === 'order') {
            await moveUserDocument('order', 'enquiry', targetUserId, enquiryId);
            await writeUserDocument('enquiry', targetUserId, updateData, enquiryId);
            setCurrentCollection('enquiry');
          }
        }
      }

      setReqState(finalReqState);
      setShowOptionDropdown(false);
      setOptionAction(null);

      if (isStopActionTriggered) {
        setIsButtonDisabled(true);
      }

      const optionEntry = exceptionTimelines.find(
        entry => entry.id.startsWith('option') && entry.requestState === finalReqState
      );
      const isActionAllowed = processControls.some(
        control =>
          rolePrefixes.some(p => control.id.startsWith(p)) &&
          control.application === 'Small' &&
          control.activity === 'Action' &&
          control.state === finalReqState
      );
      const isEnquiryButtonEnabledForCheck = isActionAllowed && (!isNormalUser || deliveryDetailsValid) && !isStopActionTriggered;

      if (optionEntry && isEnquiryButtonEnabledForCheck) {
        setShowOptionDropdown(true);
        setOptionAction(optionEntry);
      } else {
        setShowOptionDropdown(false);
        setOptionAction(null);
      }
    } catch (err) {
      console.error('Error updating reqState from option selection:', err.message, err.stack);
      showToast(`Failed to update request state: ${err.message}`);
    }
  };

  const formatDescription = () => {
    const flavourText = (flavour || 'unknown').toLowerCase();
    const capitalizedFlavour = flavourText.charAt(0).toUpperCase() + flavourText.slice(1);
    const subtitleText = (subtitle || 'none').toLowerCase().replace('filling: ', '');
    const coverText = (cover || 'none').toLowerCase();
    const spongeText = (sponge || 'unknown').toLowerCase();

    const dietaryOptions = [];
    if (eggless) dietaryOptions.push('eggless');
    if (glutenFree) dietaryOptions.push('gluten free');
    const dietaryText = dietaryOptions.length > 0 ? `${dietaryOptions.join(' and ')} ` : '';

    const descriptionLine1 = `${capitalizedFlavour} flavour with ${subtitleText} filling, covered by ${coverText} on ${dietaryText}${spongeText} sponge.`;

    const quantityText = quantity && quantity !== 'null' ? quantity : '1 kg';
    const costValue = costEstimate && costEstimate !== 'null' && costEstimate !== null && costEstimate !== '' ? costEstimate : (ccost && ccost !== 'null' && ccost !== '' ? ccost : 'N/A');
    const formattedCostValue = costValue === 'N/A' ? 'N/A' : `${getCurrencySymbol()}${costValue.replace(/[^0-9.]/g, '')}`;
    return { descriptionLine1, quantityText, formattedCostValue };
  };

  const { descriptionLine1, quantityText, formattedCostValue } = formatDescription();

  const handleClick = (e) => {
    if (onClick) {
      onClick(e);
    }
  };

  const handleDelete = (e) => {
    e.stopPropagation();
    if (onDelete) {
      onDelete();
    }
  };

  const styles = `
    .enquiry-profile-card {
      font-family: 'Helvetica Neue' !important;
      display: flex;
      flex-direction: row;
      align-items: center;
      width: 100%;
      max-width: 1200px;
      min-height: 50px;
      max-height: 60px;
      background-color: #fff;
      border-radius: 8px;
      box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
      padding: 4px 4px 4px 4px;
      margin: 2px 0;
      cursor: pointer;
      transition: background-color 0.2s;
      position: relative;
    }

    .enquiry-profile-card.expanded {
      background-color: #f0f0f0;
    }

    .enquiry-profile-card-image {
      width: 60px;
      height: 50px;
      object-fit: fill;
      border-radius: 5px;
      margin-right: 15px;
      margin-left: 5px;
    }

    .enquiry-profile-card-info {
      flex: 1;
      display: flex;
      flex-direction: row;
      align-items: center;
      min-width: 0;
      flex-wrap: wrap;
      gap: 5px;
    }

    .enquiry-quantity-price {
      display: flex;
      flex-direction: row;
      gap: 10px;
      margin-right: 0;
      margin-left: 1px;
      margin-bottom: 2px;
      flex: 0 1 auto;
      min-width: 150px;
    }

    .enquiry-date-feedback {
      display: flex;
      flex-direction: row;
      align-items: flex-start;
      gap: 5px;
      flex: 0 1 auto;
      min-width: 200px;
    }

    .enquiry-date-feedback.feedback-active {
      position: relative;
      flex-direction: row;
      align-items: center;
    }

    .enquiry-date-feedback.review-active {
      position: relative;
      flex-direction: row-reverse;
      align-items: center;
    }

    .enquiry-delivery-date-time {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: 2px;
      margin-right: 0;
      flex-shrink: 0;
    }

    .enquiry-feedback-area {
      display: flex;
      flex-direction: row;
      gap: 5px;
      flex-shrink: 0;
      align-items: center;
    }

    .enquiry-quantity-price p, .enquiry-delivery-date-time p {
      margin: 0;
      font-size: 10px;
      color: #666;
    }

    .enquiry-quantity-price p span, .enquiry-delivery-date-time p span {
      font-weight: normal;
    }

    .enquiry-review-comments {
      border: 1px solid #ddd;
      border-radius: 4px;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
      padding: 2px 4px;
      font-size: 10px;
      color: #666;
      background-color: #f9f9f9;
      max-width: 150px;
      min-height: 20px;
      margin-right: 5px;
      flex-shrink: 0;
      white-space: normal;
      overflow: visible;
      overflow-wrap: break-word;
      line-height: 1.2;
    }

    .enquiry-request-review-btn {
      margin-left: 20px;
      background: #fa4848;
      color: white;
      border: none;
      border-radius: 5px;
      cursor: pointer;
      font-size: 10px;
      white-space: nowrap;
      padding: 4px 8px;
    }

    .enquiry-request-review-btn.feedback-active {
      margin-left: 20px;
    }

    .enquiry-request-review-btn.review-active {
      margin-left: 10px;
    }

    .enquiry-request-review-btn:hover:not(:disabled) {
      background: #e03e3e;
    }

    .enquiry-request-review-btn:disabled {
      background: #cccccc;
      cursor: not-allowed;
    }

    .enquiry-action-buttons {
      display: flex;
      gap: 5px;
      margin-left: 20px;
    }

    .enquiry-accept-btn, .enquiry-reject-btn {
      background: #fa4848;
      color: white;
      border: none;
      border-radius: 5px;
      cursor: pointer;
      font-size: 10px;
      padding: 5px 8px;
    }

    .enquiry-accept-btn:hover, .enquiry-reject-btn:hover {
      background: #e03e3e;
    }

    .enquiry-option-dropdown {
      margin-left: 5px;
      font-size: 10px;
      padding: 2px;
      border-radius: 5px;
      border: 1px solid #ddd;
      background: #fff;
      cursor: pointer;
    }

    .enquiry-option-dropdown:focus {
      outline: none;
      border-color: #fa4848;
    }

    .enquiry-delete-btn {
      position: absolute;
      top: 5px;
      right: 5px;
      background: none;
      border: none;
      cursor: pointer;
      color: #666;
      font-size: 12px;
      padding: 2px;
      display: none;
      align-items: center;
      justify-content: center;
      transition: opacity 0.2s, color 0.2s;
    }

    .enquiry-profile-card:hover .enquiry-delete-btn {
      display: flex;
    }

    .enquiry-delete-btn:hover:not(:disabled) {
      color: #333;
    }

    .enquiry-delete-btn:disabled {
      opacity: 0.3;
      color: #666;
      cursor: not-allowed;
    }

    .popup-overlay {
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.5);
      display: flex;
      justify-content: center;
      align-items: center;
      z-index: 1000;
    }

    .popup-content {
      background: white;
      border-radius: 5px;
      box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
      position: relative;
      width: 300px;
    }

    .reject-popup-content {
      width: 200px;
      display: flex;
      flex-direction: column;
      background: white;
      opacity: 1;
      z-index: 1001;
    }

    .reject-popup-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 8px 12px;
      border-bottom: 1px solid #ddd;
      font-size: 14px;
      font-weight: bold;
      color: #333;
      background: #f9f9f9;
    }

    .reject-popup-body {
      padding: 12px;
      font-size: 12px;
      color: #333;
      text-align: center;
      background: white;
    }

    .reject-popup-footer {
      display: flex;
      justify-content: flex-end;
      padding: 8px 12px;
      border-top: 1px solid #ddd;
      background: #f9f9f9;
    }

    .feedback-popup-content {
      width: 250px;
      padding: 15px;
      display: flex;
      flex-direction: column;
      gap: 15px;
      position: fixed;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      z-index: 1001;
      background: white;
      opacity: 1;
    }

    .feedback-header {
      font-size: 14px;
      font-weight: bold;
      color: #333;
      text-align: center;
    }

    .star-rating {
      display: flex;
      justify-content: center;
      gap: 5px;
    }

    .star {
      font-size: 20px;
      color: #ddd;
      cursor: pointer;
    }

    .star.filled {
      color: #fa4848;
    }

    .feedback-comment {
      width: 100%;
      height: 60px;
      resize: none;
      border: 1px solid #ddd;
      border-radius: 4px;
      padding: 5px;
      font-size: 12px;
      color: #666;
    }

    .feedback-buttons {
      display: flex;
      justify-content: flex-end;
      gap: 10px;
    }

    .feedback-submit-btn, .feedback-cancel-btn {
      background: #fa4848;
      color: white;
      border: none;
      border-radius: 5px;
      cursor: pointer;
      font-size: 12px;
      padding: 5px 10px;
    }

    .feedback-submit-btn:hover, .feedback-cancel-btn:hover {
      background: #e03e3e;
    }

    .popup-close-btn {
      background: none;
      border: none;
      font-size: 16px;
      cursor: pointer;
      color: #666;
    }

    .popup-close-btn:hover {
      color: #333;
    }

    .popup-ok-btn {
      background: #fa4848;
      color: white;
      border: none;
      border-radius: 5px;
      cursor: pointer;
      font-size: 12px;
      padding: 5px 10px;
    }

    .popup-ok-btn:hover {
      background: #e03e3e;
    }

    @media (max-width: 600px) {
      .enquiry-profile-card {
        max-width: 100%;
        padding: 6px 6px 6px 3px;
        max-height: none;
      }

      .enquiry-profile-card-image {
        width: 50px;
        height: 40px;
        margin-right: 10px;
      }

      .enquiry-quantity-price p, .enquiry-delivery-date-time p {
        font-size: 8px;
      }

      .enquiry-request-review-btn {
        padding: 4px 8px;
        font-size: 8px;
      }

      .enquiry-quantity-price {
        flex-direction: column;
        gap: 2px;
        margin-right: 0;
        margin-bottom: 2px;
        min-width: 70px;
      }

      .enquiry-date-feedback {
        gap: 3px;
        min-width: 150px;
      }

      .enquiry-date-feedback.feedback-active {
        flex-direction: row;
        flex-wrap: wrap;
      }

      .enquiry-date-feedback.review-active {
        flex-direction: row;
        flex-wrap: wrap;
      }

      .enquiry-delivery-date-time {
        gap: 2px;
        margin-right: 10px;
        margin-top: 0px;
      }

      .enquiry-feedback-area {
        gap: 3px;
      }

      .enquiry-review-comments {
        font-size: 8px;
        max-width: 80px;
        min-width: 60px;
        min-height: 20px;
        margin-right: 3px;
      }

      .enquiry-request-review-btn {
        padding: 4px 8px;
        font-size: 8px;
        margin-left: 20px;
        margin-top: 4px;
      }

      .enquiry-request-review-btn.feedback-active {
        margin-left: 20px;
        margin-top: 0;
      }

      .enquiry-request-review-btn.review-active {
        margin-left: 10px;
        margin-top: 0;
      }

      .enquiry-action-buttons {
        gap: 3px;
        margin-left: 20px;
        margin-top: 4px;
      }

      .enquiry-accept-btn, .enquiry-reject-btn {
        font-size: 9px;
        padding: 4px 6px;
      }

      .enquiry-option-dropdown {
        font-size: 8px;
        padding: 1px;
        margin-left: 3px;
      }

      .enquiry-delete-btn {
        font-size: 10px;
        top: 3px;
        right: 3px;
      }

      .popup-content {
        padding: 15px;
        width: 80%;
        font-size: 14px;
      }

      .reject-popup-content {
        width: 180px;
      }

      .reject-popup-header {
        padding: 6px 10px;
        font-size: 12px;
      }

      .reject-popup-body {
        padding: 10px;
        font-size: 11px;
      }

      .reject-popup-footer {
        padding: 6px 10px;
      }

      .feedback-popup-content {
        width: 220px;
        padding: 10px;
        gap: 10px;
      }

      .feedback-header {
        font-size: 12px;
      }

      .star {
        font-size: 18px;
      }

      .feedback-comment {
        height: 50px;
        font-size: 11px;
      }

      .feedback-submit-btn, .feedback-cancel-btn {
        font-size: 11px;
        padding: 4px 8px;
      }
    }
  `;

  return (
    <>
      <div className={`enquiry-profile-card ${isExpanded ? 'expanded' : ''}`} onClick={handleClick}>
        <style>{styles}</style>
        {image ? (
          <img
            src={image}
            alt={theme || 'Profile'}
            className="enquiry-profile-card-image"
          />
        ) : (
          <div
            className="enquiry-profile-card-image"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: '#f0f0f0',
              fontSize: '10px',
              color: '#666',
            }}
          >
            No Image
          </div>
        )}

        {showDeleteIcon && (
          <button
            className="enquiry-delete-btn"
            onClick={handleDelete}
            title="Delete Profile"
            disabled={!canEnableDelete()}
          >
            <FaTrash />
          </button>
        )}

        <div className="enquiry-profile-card-info">
          <div className="enquiry-quantity-price">
            <p><span>Quantity:</span> {quantityText}</p>
            <p><span>Cost:</span> {formattedCostValue}</p>
          </div>

          <div className={`enquiry-date-feedback ${showFeedbackPopup ? 'feedback-active' : ''} ${isNormalUser && rev_feed && rev_feed.trim() !== '' ? 'review-active' : ''}`}>
            <div className="enquiry-delivery-date-time">
              <p>
                <span>{del_mode === false && (deliveryDate || deliveryTime) ? 'PickUp Date' : 'Delivery Date'}:</span>{' '}
                {deliveryDate || ''}
              </p>
              <p>
                <span>{del_mode === false && (deliveryDate || deliveryTime) ? 'PickUp Time' : 'Delivery Time'}:</span>{' '}
                {deliveryTime || ''}
              </p>
            </div>
            <div className="enquiry-feedback-area">
              {isNormalUser && rev_feed && rev_feed.trim() !== '' && (
                <div className="enquiry-review-comments">
                  {rev_feed}
                </div>
              )}
              {showChangeButtons ? (
                <div className="enquiry-action-buttons">
                  <button
                    className="enquiry-accept-btn"
                    onClick={handleAccept}
                  >
                    Accept
                  </button>
                  <button
                    className="enquiry-reject-btn"
                    onClick={handleRejectClick}
                  >
                    Reject
                  </button>
                </div>
              ) : showOptionDropdown && optionAction ? (
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <button
                    className="enquiry-request-review-btn"
                    onClick={handleRequestReview}
                    disabled={!isEnquiryButtonEnabled}
                    title={!isEnquiryButtonEnabled ? deliveryDetailsValid ? 'This action is restricted' : 'Please fill up delivery details' : ''}
                  >
                    {buttonText}
                  </button>
                  <select
                    className="enquiry-option-dropdown"
                    onChange={handleOptionSelect}
                    value=""
                  >
                    <option value="" disabled>Select an option</option>
                    <option value={optionAction.showState1}>{optionAction.showState1}</option>
                    <option value={optionAction.showState2}>{optionAction.showState2}</option>
                  </select>
                </div>
              ) : (
                <button
                  className={`enquiry-request-review-btn ${showFeedbackPopup ? 'feedback-active' : ''} ${isNormalUser && rev_feed && rev_feed.trim() !== '' ? 'review-active' : ''}`}
                  onClick={handleRequestReview}
                  disabled={!isEnquiryButtonEnabled}
                  title={!isEnquiryButtonEnabled ? deliveryDetailsValid ? 'This action is restricted' : 'Please fill up delivery details' : ''}
                >
                  {buttonText}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {showPopup && (
        <div className="popup-overlay">
          <div className="popup-content">
            <button
              className="popup-close-btn"
              onClick={() => setShowPopup(false)}
            >
              ×
            </button>
            <p>
              {deliveryDetailsValid
                ? 'This action is restricted'
                : 'Please fill up delivery details'}
            </p>
          </div>
        </div>
      )}

      {showRejectPopup && (
        <div className="popup-overlay">
          <div className="reject-popup-content">
            <div className="reject-popup-header">
              <span>Confirm Rejection</span>
              <button
                className="popup-close-btn"
                onClick={() => setShowRejectPopup(false)}
              >
                ×
              </button>
            </div>
            <div className="reject-popup-body">
              <p>Request will be Cancelled</p>
            </div>
            <div className="reject-popup-footer">
              <button
                className="popup-ok-btn"
                onClick={handleRejectConfirm}
              >
                Ok
              </button>
            </div>
          </div>
        </div>
      )}

      {showFeedbackPopup && (
        <div className="popup-overlay">
          <div className="feedback-popup-content">
            <div className="feedback-header">
              Provide Your Feedback
            </div>
            <div>
              <label style={{ fontSize: '12px', color: '#333', display: 'block', textAlign: 'center', marginBottom: '5px' }}>
                Overall Satisfaction
              </label>
              <div className="star-rating">
                {[1, 2, 3, 4, 5].map((star) => (
                  <FaStar
                    key={star}
                    className={`star ${feedbackRating >= star ? 'filled' : ''}`}
                    onClick={() => handleStarClick(star)}
                  />
                ))}
              </div>
            </div>
            <div>
              <label style={{ fontSize: '12px', color: '#333', display: 'block', textAlign: 'center', marginBottom: '5px' }}>
                Describe your experience {feedbackRating < 4 && feedbackRating > 0 ? '(required)' : '(optional)'}
              </label>
              <textarea
                className="feedback-comment"
                value={feedbackComment}
                onChange={(e) => setFeedbackComment(e.target.value)}
                placeholder="Enter your feedback here..."
              />
            </div>
            <div className="feedback-buttons">
              <button
                className="feedback-cancel-btn"
                onClick={() => {
                  setShowFeedbackPopup(false);
                  setFeedbackRating(0);
                  setFeedbackComment('');
                }}
              >
                Cancel
              </button>
              <button
                className="feedback-submit-btn"
                onClick={handleFeedbackSubmit}
              >
                Submit
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default React.memo(EnquiryProfileCard);